<template>
  <!--
    Our <Dialog> component isn't very ideal for this as we want the preview
    iframe to take as much space as it can, which it can't do, so let's make
    this a special excemption.
  -->
  <Teleport to="#app-scaffold">
    <div class="fixed inset-0 z-dialog flex flex-col bg-background">
      <div
        class="flex h-[53px] shrink-0 items-center justify-between gap-4 border-b border-border bg-background px-4 transition"
      >
        <span class="truncate text-sm font-bold"> Email preview </span>

        <div class="flex items-center gap-2">
          <Menu.Dropdown :label="contextLabel">
            <Menu.SectionHeader>Form</Menu.SectionHeader>
            <Menu.CheckboxItem
              v-for="context in relevantContexts"
              :key="context"
              :model-value="mode === context"
              :label="CONTEXT_TO_CONFIGURATION[context].title"
              :icon="CONTEXT_TO_CONFIGURATION[context].icon"
              variant="radio"
              @update:model-value="mode = context"
            />

            <Menu.SectionHeader>Device presets</Menu.SectionHeader>
            <Menu.CheckboxItem
              v-for="dev in RESPONSIVE_DEVICES"
              :key="dev.key"
              :model-value="dev.key === responsiveSize.key"
              :label="dev.name"
              :icon="dev.icon"
              variant="radio"
              @update:model-value="
                responsiveSize = {
                  key: dev.key,
                  width: dev.width,
                  height: dev.height,
                }
              "
            />
          </Menu.Dropdown>

          <Tooltip.Hint text="Close">
            <button
              type="button"
              aria-label="Close"
              class="-mr-1 rounded-full p-[0.4375rem] text-gray-700 outline-2 outline-action transition hover:bg-gray-200 focus-visible:outline active:bg-gray-200 aria-expanded:bg-gray-200 dark:text-muted-foreground dark:hover:bg-card dark:active:bg-card dark:aria-expanded:bg-card"
              @click="emit('close')"
            >
              <XMarkIcon class="size-4" />
            </button>
          </Tooltip.Hint>
        </div>
      </div>

      <ResizableIframe v-model:responsive-size="responsiveSize">
        <EmailPreview
          :pending="pending"
          :error="error"
          :result="result"
          :mode="mode"
          :show-class-highlights="showClassHighlights"
        />
      </ResizableIframe>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";

import Menu from "@/design_system/Menu";
import Tooltip from "@/design_system/Tooltip";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";
import type { RenderResult } from "@/lib/api/queries/emails/render";
import { useLocalStorageRef } from "@/lib/composables/local-storage";
import { LocalStorageKey } from "@/store/local_storage";
import { useNewsletter } from "@/store/stores/account";
import {
  type PreviewContext,
  useStore as useUiStatesStore,
} from "@/store/stores/ui-states";

import ResizableIframe from "./components/ResizableIframe.vue";
import EmailPreview from "./EmailPreview.vue";
import { type ResponsiveSize, RESPONSIVE_DEVICES } from "./responsive";
import { CONTEXT_TO_CONFIGURATION } from "./types";

const newsletter = useNewsletter();

const props = withDefaults(
  defineProps<{
    pending?: boolean;
    error?: unknown;
    result: RenderResult | undefined;

    contexts?: PreviewContext[];
    showClassHighlights?: boolean;
  }>(),
  {
    contexts: () => ["email", "web"],
  }
);

const emit = defineEmits<{
  close: [];
}>();

const uiStates = useUiStatesStore();

const mode = computed<PreviewContext>({
  get: () => {
    const stored = uiStates.previewContext;
    if (stored && props.contexts.includes(stored)) {
      return stored;
    }
    return props.contexts[0] ?? "email";
  },
  set: (v) => {
    uiStates.previewContext = v;
  },
});

const responsiveSize = useLocalStorageRef<ResponsiveSize>(
  LocalStorageKey.PREVIEW_RESPONSIVE_SIZE,
  {
    parse: (value) => {
      if (value) {
        try {
          const parsed = JSON.parse(value);
          if (
            parsed &&
            typeof parsed.width === "number" &&
            typeof parsed.height === "number"
          ) {
            return parsed as ResponsiveSize;
          }
        } catch {
          // Invalid JSON, fall through to default
        }
      }
      return { width: 540, height: 720 };
    },
    stringify: (value) => JSON.stringify(value),
  }
);

const responsiveDevice = computed(() => responsiveSize.value.key);

// Ensure mode is valid when contexts change
watch(
  () => props.contexts,
  () => {
    if (!props.contexts.includes(mode.value)) {
      mode.value = props.contexts[0];
    }
  },
  { immediate: true }
);

const relevantContexts = computed(() => {
  return props.contexts.filter((context) =>
    context === "web" || context === "archive"
      ? newsletter.value.enabled_features.includes("archives")
      : context === "portal"
      ? newsletter.value.enabled_features.includes("portal")
      : true
  );
});

const contextLabel = computed(() => {
  const key = responsiveDevice.value;

  const config = CONTEXT_TO_CONFIGURATION[mode.value];
  const device = key && RESPONSIVE_DEVICES.find((dev) => dev.key === key);

  return config.title + (device ? ` · ` + device.name : "");
});
</script>
